// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Il bilancio di massa"
// Centro assistenza → guida per amministratori, solo piano TRACE.
//
// La schermata confronta quanto e' entrato in una lavorazione con quanto ne e'
// uscito. E' l'unica dove il programma esprime un GIUDIZIO, quindi e' anche
// quella dove un'imprecisione nella guida fa piu' danni.
//
// DUE COSE DA NON CONFONDERE, ed e' il cuore della guida:
//   RESA = output diviso input. Numero NEUTRO: la resa "giusta" dipende dal
//          prodotto (un prosciutto in stagionatura perde il 30% ed e'
//          fisiologico). Il programma non ha modo di giudicarla.
//   SCOSTAMENTO = quanto il prodotto ottenuto si discosta dalla QUANTITA'
//          PREVISTA della lavorazione. E' questo che vale Ottimale /
//          Tollerabile / Critica.
// Fino al 19/08/2026 la resa era colorata col criterio dello scostamento, e
// quindi una resa fisiologica sembrava sbagliata. Corretto: se qualcuno
// ricolora quel numero, il capitolo 3 va riscritto.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette dal codice.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ....... App.js, BilancioMassa
//   Soglie .......... scostPerc <= 0.05 Ottimale · <= 0.15 Tollerabile ·
//                     oltre Critica (valori assoluti, non configurabili)
//   FONTE ........... FONTE_QTA_LABEL: dichiarata->'misurata' (verde),
//                     stimata->'da ricetta' (blu), lotto->'lotto intero'
//                     (arancio). La priorita' e' decisa dal backend:
//                     GET /lavorazioni/:id/bilancio, COALESCE(quantita_usata,
//                     stima da ricetta riscalata, intera quantita' del lotto).
//   Conversione ..... toKg() converte SOLO kg, g, l/lt/L, ml. Tutto il resto
//                     (pz, conf, cartoni, sacchi) NON e' convertibile: quelle
//                     righe sono ESCLUSE dai totali e contate nell'avviso
//                     arancione. E' un limite vero, non un difetto: senza un
//                     peso per pezzo la conversione non esiste.
//   Elenco .......... solo lavorazioni CHIUSE (stato=CHIUSA nella chiamata)
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, SchermoTitolo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'b0', label: 'A cosa serve' },
  { id: 'b1', label: '1. Da dove arrivano i numeri' },
  { id: 'b2', label: '2. Leggere la scheda' },
  { id: 'b3', label: '3. Resa e scostamento' },
  { id: 'b4', label: '4. Le unità non convertibili' },
  { id: 'b5', label: '5. Gli scarti' },
  { id: 'b6', label: '6. Quando esce “Critica”' },
  { id: 'b7', label: 'Le trappole' },
  { id: 'b8', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const ChipFinto = ({ children, tono = 'neutro' }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    arancio: { fg: LT.arancio, bg: LT.arancioSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    ciano:   { fg: LT.ciano,   bg: LT.cianoSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const Kpi = ({ etichetta, valore, unita, sotto, colore }) => (
  <div>
    <div style={{
      fontSize: 10, fontWeight: 700, color: LT.n500,
      letterSpacing: 0.6, marginBottom: 4, textTransform: 'uppercase',
    }}>{etichetta}</div>
    <div style={{ fontSize: 21, fontWeight: 800, color: colore || LT.navy }}>
      {valore}
      {unita && <span style={{ fontSize: 12, color: LT.n500, fontWeight: 700 }}> {unita}</span>}
    </div>
    {sotto && <div style={{ fontSize: 10, color: LT.n500, marginTop: 2 }}>{sotto}</div>}
  </div>
);

const TH = ({ children, destra }) => (
  <th style={{
    padding: '7px 9px', textAlign: destra ? 'right' : 'left',
    fontSize: 9.5, fontWeight: 700, color: LT.n500,
    textTransform: 'uppercase', letterSpacing: 0.4,
    borderBottom: `1px solid ${LT.n200}`, whiteSpace: 'nowrap',
  }}>{children}</th>
);

const TD = ({ children, destra, mono }) => (
  <td style={{
    padding: '8px 9px', textAlign: destra ? 'right' : 'left',
    fontSize: 12, color: LT.navy,
    fontFamily: mono ? LT.mono : LT.sans,
  }}>{children}</td>
);

const Sezione = ({ colore, icona, titolo, sotto, children }) => (
  <div style={{
    background: LT.n0, border: `1px solid ${LT.n200}`,
    borderRadius: LT.r.md, padding: 13, marginBottom: 11,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 11 }}>
      <div style={{
        width: 28, height: 28, borderRadius: LT.r.md,
        background: `${colore}1a`, display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Ico d={icona} size={15} stroke={colore} />
      </div>
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: LT.navy }}>{titolo}</div>
        {sotto && <div style={{ fontSize: 10.5, color: LT.n500, marginTop: 1 }}>{sotto}</div>}
      </div>
    </div>
    {children}
  </div>
);

export default function BilancioMassa() {
  return (
    <GuidaLayout
      titolo="Il bilancio di massa"
      sottotitolo="Quanto è entrato in una lavorazione, quanto ne è uscito, e quanto quel confronto si discosta da quello che avevate previsto. È l'unica schermata in cui il programma dà un giudizio — e serve sapere esattamente su cosa lo dà."
      durata="~ 12 minuti"
      destinatario="Per amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* b0 */}
      <H id="b0">A cosa serve</H>

      <P>
        <Code>Bilancio di massa</Code>, nel gruppo Verifiche e controlli, mette a confronto
        due numeri per ogni lavorazione chiusa: <Strong>quanto materiale è entrato</Strong> e{' '}
        <Strong>quanto prodotto ne è uscito</Strong>.
      </P>
      <P>
        Serve a due cose diverse. La prima è di mestiere: accorgersi che una produzione è
        andata storta, che si è sprecato più del solito, che un lotto ha reso meno degli
        altri. La seconda è formale: un ispettore che sospetta un'irregolarità confronta
        quanto avete comprato con quanto avete venduto, e se i due numeri non si parlano
        chiede spiegazioni.
      </P>

      <Callout kind="info" titolo="Solo lavorazioni chiuse">
        L'elenco mostra soltanto le lavorazioni <Strong>Chiuse</Strong>. È inevitabile: il
        lotto di prodotto finito nasce alla chiusura, quindi prima non c'è nessun output da
        confrontare. Se cercate una produzione e non la trovate, quasi sempre è ancora
        aperta.
      </Callout>

      {/* b1 */}
      <H id="b1" cat="produzione">1. Da dove arrivano i numeri</H>

      <P>
        È il capitolo più importante di questa guida, ed è quello che rende leggibile tutto
        il resto. <Strong>Il programma non pesa niente.</Strong> Il numero che chiama "input"
        lo ricava, per ogni lotto agganciato alla lavorazione, seguendo tre strade in ordine
        di preferenza. Su ogni riga c'è un contrassegno, la colonna <Strong>Fonte</Strong>,
        che dice quale delle tre è stata usata.
      </P>

      <Passo n="1" titolo={<>Misurata <ChipFinto tono="verde">misurata</ChipFinto></>}>
        <P style={{ margin: '4px 0' }}>
          Avete dichiarato voi la quantità usata di quel lotto, nella schermata della
          lavorazione. È il dato migliore che esista: viene da una persona che era lì.
        </P>
      </Passo>

      <Passo n="2" titolo={<>Da ricetta <ChipFinto tono="blu">da ricetta</ChipFinto></>}>
        <P style={{ margin: '4px 0' }}>
          Non avete dichiarato niente, ma la ricetta prevede quell'ingrediente: il programma
          calcola la quantità della ricetta <Strong>riscalata</Strong> sulla dimensione della
          lavorazione. È una stima ragionevole, non una misura.
        </P>
      </Passo>

      <Passo n="3" titolo={<>Lotto intero <ChipFinto tono="arancio">lotto intero</ChipFinto></>}>
        <P style={{ margin: '4px 0' }}>
          Non avete dichiarato niente <em>e</em> quell'ingrediente non è in ricetta: al
          programma non resta che contare <Strong>tutta la quantità del lotto</Strong>. Quasi
          sempre è troppo — un sacco di sale da 25 kg agganciato a una lavorazione che ne ha
          usati due conta 25.
        </P>
      </Passo>

      <Callout kind="warn" titolo="Il contrassegno arancione è un avviso, non un dettaglio">
        Se in una scheda compaiono righe <ChipFinto tono="arancio">lotto intero</ChipFinto>,
        l'input totale è gonfiato e <Strong>la resa risulta più bassa del vero</Strong>. Non è
        un errore del programma: è il programma che vi dice che gli mancava il dato. Si
        risolve tornando nella lavorazione e dichiarando la quantità usata, e il bilancio si
        ricalcola da solo.
      </Callout>

      {/* b2 */}
      <H id="b2" cat="produzione">2. Leggere la scheda</H>

      <MockFrame
        url="app.labthree.it — Bilancio di massa · dettaglio"
        markers={[
          { n: 1, top: '26%', left: '86%', titolo: 'Il giudizio',
            testo: 'Riguarda lo scostamento dalla quantità prevista, non la resa. L’etichetta accanto lo dice.' },
          { n: 2, top: '45%', left: '52%', titolo: 'La resa',
            testo: 'Neutra di proposito: output diviso input. Il programma non ha modo di sapere quale sia la resa giusta per il vostro prodotto.' },
          { n: 3, top: '80%', left: '86%', titolo: 'Fonte',
            testo: 'Da leggere sempre. Dice se quel numero è misurato, stimato dalla ricetta, o l’intero lotto per mancanza di dati.' },
        ]}
      >
        <Schermo>
          <SchermoTitolo
            titolo="Prosciutto crudo stagionato"
            sottotitolo="Lotto L. 23 PT · lav. #214 · chiusa"
          />

          <div style={{
            background: LT.verdeSoft, borderLeft: `4px solid ${LT.verde}`,
            borderRadius: LT.r.md, padding: 14, marginBottom: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 11, flexWrap: 'wrap' }}>
              <div style={{
                width: 28, height: 28, borderRadius: LT.r.md, background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Ico d={I.trend} size={15} stroke={LT.verde} />
              </div>
              <div style={{ fontSize: 12, fontWeight: 800, color: LT.navy, letterSpacing: 0.4 }}>
                RESA E SCOSTAMENTO
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontSize: 10, color: LT.n500, fontWeight: 600 }}>scostamento dal previsto</span>
                <ChipFinto tono="verde">Ottimale</ChipFinto>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <Kpi etichetta="Input totale"    valore="2 410.000" unita="kg" />
              <Kpi etichetta="Output PF"       valore="1 687.000" unita="kg" />
              <Kpi etichetta="Resa"            valore="70.0%"     sotto="output ÷ input" />
              <Kpi etichetta="Teorica ricetta" valore="1 700.000" unita="kg" sotto="Δ −13.000 kg" colore={LT.n700} />
            </div>
          </div>

          <Sezione colore={LT.blu} icona={I.package} titolo="Input — Materie prime" sotto="3 righe consumate">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: LT.n50 }}>
                <TH>Ingrediente</TH><TH>Lotto</TH><TH>Fornitore</TH><TH destra>Qtà usata</TH><TH destra>Fonte</TH>
              </tr></thead>
              <tbody>
                {[
                  ['Coscia suina fresca', 'L.120626', 'Mec Carni S.p.A.', '2 380 kg', 'verde',   'misurata'],
                  ['Sale marino grosso',  'L.030826', 'Saline Trapani',   '28 kg',    'blu',     'da ricetta'],
                  ['Sugna',               'L.150726', 'Suinalp Srl',      '2 kg',     'arancio', 'lotto intero'],
                ].map((r, i) => (
                  <tr key={i} style={{ borderBottom: i < 2 ? `1px solid ${LT.n100}` : 'none' }}>
                    <TD>{r[0]}</TD>
                    <TD mono>{r[1]}</TD>
                    <TD>{r[2]}</TD>
                    <TD destra>{r[3]}</TD>
                    <TD destra><ChipFinto tono={r[4]}>{r[5]}</ChipFinto></TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </Sezione>

          <Sezione colore={LT.viola} icona={I.check} titolo="Output — Prodotto finito" sotto="1 lotto generato">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: LT.n50 }}>
                <TH>Prodotto</TH><TH>Lotto</TH><TH>Data</TH><TH destra>Quantità</TH>
              </tr></thead>
              <tbody><tr>
                <TD>Prosciutto crudo stagionato</TD>
                <TD mono>L. 23 PT</TD>
                <TD>18/12/2027</TD>
                <TD destra>1 687 kg</TD>
              </tr></tbody>
            </table>
          </Sezione>
        </Schermo>
      </MockFrame>

      <P>
        Sotto ci sono altre due sezioni quando servono: <Strong>Input — Semilavorati</Strong>,
        se la lavorazione ne ha consumati, e <Strong>Scarti documentati</Strong>, di cui al
        capitolo 5.
      </P>

      {/* b3 */}
      <H id="b3" cat="produzione">3. Resa e scostamento sono due numeri diversi</H>

      <P>
        È la confusione più facile da fare, e vale la pena leggerla due volte.
      </P>

      <div style={{
        border: `1px solid ${LT.n200}`, borderRadius: LT.r.md,
        margin: '18px 0', overflow: 'hidden',
      }}>
        {[
          ['Resa', 'output ÷ input', 'Quanto prodotto avete ottenuto da quanto materiale. Non è colorata e non ha un giudizio: la resa giusta dipende dal prodotto.', LT.navy],
          ['Scostamento', 'output − quantità prevista', 'Di quanto il risultato si allontana da quello che avevate previsto aprendo la lavorazione. È questo che vale Ottimale, Tollerabile o Critica.', LT.arancio],
        ].map(([t, f, d, col], i) => (
          <div key={t} style={{
            padding: '13px 16px',
            borderBottom: i === 0 ? `1px solid ${LT.n200}` : 'none',
            background: i === 0 ? LT.n0 : LT.n50,
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: col }}>{t}</span>
              <Code>{f}</Code>
            </div>
            <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6, marginTop: 6 }}>{d}</div>
          </div>
        ))}
      </div>

      <Callout kind="tip" titolo="Perché la resa non ha un colore">
        Un prosciutto che entra a 12 kg ed esce a 8 dopo diciotto mesi ha una resa del 67%, ed
        è <em>perfetta</em>: quel calo è la stagionatura. Una mozzarella con la stessa resa
        sarebbe un disastro. Il programma non sa che prodotto state facendo, quindi non ha
        titolo per giudicare quel numero — e colorarlo di rosso vi farebbe cercare un problema
        che non esiste. <Strong>Il metro di paragone giusto siete voi</Strong>: la resa di
        questa lavorazione confrontata con quella delle vostre lavorazioni precedenti dello
        stesso prodotto.
      </Callout>

      <P>Le tre soglie del giudizio, che riguardano <em>solo</em> lo scostamento:</P>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '16px 0' }}>
        {[
          ['Ottimale', 'fino al 5%', 'verde'],
          ['Tollerabile', 'dal 5% al 15%', 'arancio'],
          ['Critica', 'oltre il 15%', 'rosso'],
        ].map(([n, s, tono]) => (
          <div key={n} style={{
            flex: '1 1 170px', border: `1px solid ${LT.n200}`,
            borderRadius: LT.r.md, padding: '12px 14px', background: LT.n0,
          }}>
            <ChipFinto tono={tono}>{n}</ChipFinto>
            <div style={{ fontSize: 13, color: LT.n700, marginTop: 7 }}>{s}</div>
          </div>
        ))}
      </div>

      <P>
        Lo scostamento si misura sulla <Strong>quantità prevista</Strong> che avete indicato
        aprendo la lavorazione. Se non l'avete indicata, il programma ripiega sulla quantità
        base della ricetta. Ne segue una conseguenza pratica: <Strong>una quantità prevista
        scritta a caso produce un giudizio senza senso</Strong>, in un verso o nell'altro.
      </P>

      {/* b4 */}
      <H id="b4" cat="pulizie">4. Le unità che non si possono sommare</H>

      <P>
        Un bilancio di massa somma pesi. Il programma converte in chili quello che può —{' '}
        <Code>kg</Code>, <Code>g</Code>, <Code>l</Code>, <Code>ml</Code> — e{' '}
        <Strong>esclude dal calcolo</Strong> tutto il resto: pezzi, confezioni, cartoni,
        sacchi. Quando succede compare un avviso arancione che dice quante righe sono state
        lasciate fuori.
      </P>

      <Callout kind="warn" titolo="Non è un difetto: è che la conversione non esiste">
        Per trasformare 40 pezzi in chili servirebbe il peso di un pezzo, e quel dato in
        LabThree non c'è — né potrebbe esserci, visto che due prosciutti non pesano uguale.
        Preferiamo escludere quelle righe e dirlo, piuttosto che inventare un numero e
        presentarlo come un bilancio.
      </Callout>

      <P>
        La conseguenza pratica è che <Strong>il bilancio ha senso quanto le unità che
        usate</Strong>. Se lavorate a pezzi e volete un bilancio leggibile, la strada è
        registrare i lotti in chili dove è possibile — la bolla d'ingresso li accetta, ed è
        il momento in cui la merce è sulla bilancia comunque.
      </P>

      {/* b5 */}
      <H id="b5" cat="pulizie">5. Gli scarti documentati</H>

      <P>
        L'ultima sezione elenca quello che è stato <Strong>scartato</Strong> durante la
        lavorazione, con il motivo e la quantità. Sono gli scarti registrati dalla schermata
        della lavorazione, quelli che aprono una non conformità.
      </P>
      <P>
        Il loro ruolo qui è preciso: <Strong>spiegano la differenza</Strong>. Una lavorazione
        che rende meno del previsto e che ha 40 kg di scarto documentato con motivazione non è
        un buco — è una lavorazione andata storta e registrata onestamente. La stessa
        differenza <em>senza</em> scarti documentati è la domanda che vi farebbe un ispettore:
        dov'è finita la materia prima mancante?
      </P>

      <Callout kind="tip" titolo="È il motivo per cui conviene registrare gli scarti">
        Registrare uno scarto sembra ammettere un errore, e istintivamente si evita. In realtà
        è il contrario: uno scarto documentato <Strong>chiude</Strong> una domanda, uno scarto non
        registrato la lascia aperta e trasforma un incidente normale in un'irregolarità
        apparente.
      </Callout>

      {/* b6 */}
      <H id="b6" cat="manutenzioni">6. Quando esce “Critica”</H>

      <P>
        <Strong>Non è un errore, è una domanda.</Strong> Il programma sta dicendo che avete
        ottenuto più del 15% in meno — o in più — di quello che vi aspettavate. Le cause, in
        ordine di frequenza reale:
      </P>

      <div style={{ margin: '18px 0' }}>
        {[
          ['Manca una quantità usata', 'Ci sono righe "lotto intero": l\'input è gonfiato e lo scostamento pure. È la causa numero uno, e si risolve in un minuto.'],
          ['La quantità prevista era sbagliata', 'Scritta di fretta all\'apertura della lavorazione e mai corretta. Il giudizio confronta con quella.'],
          ['Unità non convertibili escluse', 'Se metà dell\'input è in pezzi, il totale in chili è parziale e il confronto non regge.'],
          ['Uno scarto non registrato', 'Del prodotto è stato buttato ma non è stato scritto da nessuna parte.'],
          ['È andata davvero male', 'Ed è l\'unico caso in cui la schermata ha fatto il suo lavoro fino in fondo: adesso lo sapete.'],
        ].map(([t, d], i) => (
          <div key={i} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '10px 0', borderBottom: i < 4 ? `1px solid ${LT.n150}` : 'none',
          }}>
            <span style={{
              width: 22, height: 22, borderRadius: 999, background: LT.n100, color: LT.n700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 2,
            }}>{i + 1}</span>
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      <Callout kind="info" titolo="Il bilancio non blocca niente">
        Nessuna delle tre valutazioni impedisce di fare qualcosa. Non ci sono lavorazioni da
        sbloccare né approvazioni da dare: è una schermata di sola lettura che guarda indietro.
        Serve a farvi accorgere, non a fermarvi.
      </Callout>

      {/* b7 */}
      <H id="b7">Le trappole</H>

      <Callout kind="warn" titolo="Il giudizio non è sulla resa">
        Vale la pena ripeterlo perché è la trappola principale: <ChipFinto tono="rosso">Critica</ChipFinto>{' '}
        non vuol dire "hai reso poco", vuol dire "hai ottenuto molto meno o molto più di
        quello che avevi previsto". Una resa bassissima ma prevista risulta{' '}
        <ChipFinto tono="verde">Ottimale</ChipFinto>.
      </Callout>

      <Callout kind="warn" titolo="Il bilancio vale quanto le quantità dichiarate">
        Se nessuno dichiara le quantità usate, il programma stima o conta lotti interi, e il
        bilancio diventa un esercizio. La colonna <Strong>Fonte</Strong> serve esattamente a
        farvi capire quanto fidarvi di quella schermata: tutte verdi vuol dire che il numero è
        vero.
      </Callout>

      <Callout kind="warn" titolo="Le lavorazioni aperte non ci sono">
        E non compariranno finché non le chiudete. Se una produzione manca dall'elenco,
        controllate il suo stato prima di cercare altrove.
      </Callout>

      <Callout kind="warn" titolo="Modificare la ricetta cambia i bilanci vecchi">
        Le stime "da ricetta" vengono ricalcolate ogni volta che aprite la schermata, sulla
        ricetta di <em>adesso</em>. Se la modificate, i bilanci delle lavorazioni già chiuse
        che si appoggiavano a quelle stime cambiano. Le righe{' '}
        <ChipFinto tono="verde">misurata</ChipFinto> invece non si muovono mai — un motivo in
        più per dichiarare le quantità.
      </Callout>

      {/* b8 */}
      <H id="b8">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="La resa mi sembra bassissima ma il prodotto è venuto bene.">
          Guardate la colonna <Strong>Fonte</Strong>. Se ci sono righe{' '}
          <ChipFinto tono="arancio">lotto intero</ChipFinto> l'input è gonfiato: il programma
          ha contato interi lotti di ingredienti di cui avete usato una parte. Dichiarate le
          quantità usate nella lavorazione e il numero si sistema da solo.
        </FAQ>

        <FAQ q="Posso cambiare le soglie 5% e 15%?">
          No, sono fisse. Sono pensate per essere generiche, e proprio per questo il giudizio
          va letto come un semaforo che invita a guardare, non come una misura di qualità del
          vostro processo.
        </FAQ>

        <FAQ q="Perché l'input non torna con quello che ho comprato?">
          Perché l'input del bilancio è quello di <em>questa</em> lavorazione, non del
          magazzino. Un lotto usato in tre lavorazioni compare in tre bilanci, ciascuno con la
          sua parte. Per il conto complessivo di un lotto c'è il{' '}
          <Tasto>Dettaglio consumi</Tasto> nel magazzino materie prime.
        </FAQ>

        <FAQ q="Il bilancio si stampa?">
          Non c'è una stampa dedicata: si stampa la pagina dal browser. Il documento che si
          porta a un'ispezione è il <Strong>test di tracciabilità</Strong>, che ricostruisce
          l'intera catena di un lotto; il bilancio è uno strumento di controllo interno.
        </FAQ>

        <FAQ q="Ho lavorato in conto terzi: cambia qualcosa?">
          Nel calcolo no, la massa è massa. In alto compare il contrassegno{' '}
          <ChipFinto tono="arancio">Conto terzi</ChipFinto> col nome del committente, ed è
          utile averlo sotto gli occhi: su una lavorazione a balia la resa è un numero che il
          committente potrebbe chiedervi, perché riguarda merce sua.
        </FAQ>

        <FAQ q="Devo controllarlo per ogni lavorazione?">
          Non serve. È utile a campione — una lavorazione ogni tanto per prodotto — e ogni
          volta che qualcosa vi è sembrato strano in produzione. Il valore non sta nel numero
          singolo ma nel confronto fra lavorazioni dello stesso prodotto nel tempo.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Il test di tracciabilità</Strong>: la simulazione di richiamo a monte e a
            valle, e cosa stampare per l'ispettore.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
